/**
 * Поведение страниц документации: аккордеон левого меню, поиск по разделам, копирование
 * кода и адресов, разворот длинных справочников и подсветка читаемого раздела.
 *
 * Всё, что можно отдать разметке, отдано разметке: раздел текущей страницы раскрыт сервером
 * (aria-expanded="true"), свёрнутый справочник свёрнут классом, кнопка копирования видна без
 * скрипта. Скрипт только добавляет поведение — без него страница остаётся читаемой.
 */

// Импортом, а не через window.bootstrap: сборка лендинга кладёт bootstrap в бандл модулем,
// глобального объекта на странице нет (в main.js и form-hendler.js он импортируется так же).
import { Offcanvas } from 'bootstrap';

/** Сколько висит всплывашка «Скопировано» и сколько длится подскок кнопки. */
const TOAST_VISIBLE_MS = 2000;
const POP_ANIMATION_MS = 200;

/** Сколько результатов поиска показываем: список длиннее выпадашки всё равно не влезает. */
const SEARCH_LIMIT = 8;

/** С какой длины запроса ищем: одна буква даёт почти весь индекс и толку от неё нет. */
const SEARCH_MIN_LENGTH = 2;

/**
 * Аккордеон разделов. Свой, а не bootstrap collapse: раздел текущей страницы должен быть
 * раскрыт СРАЗУ, до загрузки JS, — иначе на каждом переходе читатель видел бы схлопнутое
 * меню и мигание.
 */
function initAccordion(root) {
  root.querySelectorAll('[data-docs-accordion]').forEach((toggle) => {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));

    if (!panel) {
      return;
    }

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';

      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      panel.hidden = expanded;
    });
  });
}

/**
 * Всплывашка «Скопировано» — одна на страницу, заводится при первом копировании.
 *
 * Разметкой её не отдаём: до первого нажатия она не нужна, а блоков кода на странице десяток,
 * и пустой контейнер лежал бы в каждом.
 */
let toastNode = null;
let toastTimer = null;

function showCopiedToast(text) {
  if (!toastNode) {
    toastNode = document.createElement('div');
    toastNode.className = 'docs-copy-toast';
    toastNode.setAttribute('role', 'status');
    document.body.appendChild(toastNode);
  }

  toastNode.textContent = text;

  // Класс вешаем следующим кадром: элемент, добавленный и показанный в одном кадре,
  // появляется скачком — переходу не от чего отсчитывать начальное состояние.
  window.requestAnimationFrame(() => toastNode.classList.add('is-visible'));

  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastNode.classList.remove('is-visible'), TOAST_VISIBLE_MS);
}

/**
 * Копирование через скрытое поле — единственный способ, когда Clipboard API недоступен.
 *
 * Он недоступен не только в старых браузерах: navigator.clipboard есть лишь в защищённом
 * контексте (https или localhost), а стенды поднимают по http. Тот же приём, что у CopyBtn
 * в кабинете мерчанта.
 */
function copyFallback(text) {
  const field = document.createElement('textarea');

  field.value = text;
  field.setAttribute('readonly', 'readonly');
  field.style.position = 'fixed';
  field.style.top = '-1000px';
  field.style.opacity = '0';
  document.body.appendChild(field);

  let copied = false;

  try {
    field.select();
    copied = document.execCommand('copy');
  } catch (error) {
    copied = false;
  }

  field.remove();

  return copied;
}

/**
 * Копирование в буфер.
 *
 * Значение берётся из data-атрибута, а не из соседнего узла: вокруг видимого текста живёт
 * вёрстка с пробелами и переносами, и скопировалось бы не то.
 *
 * Всплывашка показывается ТОЛЬКО когда копирование реально прошло: иначе «Скопировано» врёт,
 * а буфер пуст. Подскок кнопки — наоборот, сразу по нажатию: он отвечает на «я нажал», а не
 * на «получилось».
 */
function initCopy(root) {
  root.querySelectorAll('[data-docs-copy]').forEach((button) => {
    button.addEventListener('click', async () => {
      const text = button.dataset.docsCopy || '';
      const done = button.dataset.docsCopied || '';

      button.classList.remove('docs-copy--pop');
      // Перезапуск анимации: без чтения offsetWidth браузер не замечает снятия класса
      // в том же кадре и второе нажатие подряд проходит без подскока.
      void button.offsetWidth;
      button.classList.add('docs-copy--pop');
      window.setTimeout(() => button.classList.remove('docs-copy--pop'), POP_ANIMATION_MS);

      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(text);
          showCopiedToast(done);

          return;
        } catch (error) {
          // Отказ бывает и в защищённом контексте (нет разрешения, вкладка не в фокусе) —
          // проваливаемся в старый способ, а не молчим.
        }
      }

      if (copyFallback(text)) {
        showCopiedToast(done);
      }
    });
  });
}

/** Разворот свёрнутого списка (справочник валют). Подпись кнопки меняется вместе с состоянием. */
function initToggles(root) {
  root.querySelectorAll('[data-docs-toggle]').forEach((toggle) => {
    const target = document.getElementById(toggle.getAttribute('aria-controls'));
    const label = toggle.querySelector('[data-docs-toggle-label]');

    if (!target) {
      return;
    }

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';

      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      target.classList.toggle('is-collapsed', expanded);

      if (label) {
        label.textContent = expanded
          ? toggle.dataset.docsLabelCollapsed || label.textContent
          : toggle.dataset.docsLabelExpanded || label.textContent;
      }
    });
  });
}

/**
 * Поиск по документации.
 *
 * Клиентский: индекс уже отдан вместе со страницей (<script id="docsSearchIndex">), разделов
 * единицы, и запрос на сервер за подсказкой на каждое нажатие клавиши обошёлся бы дороже
 * самого индекса.
 *
 * Совпадение — вхождение подстроки в нижнем регистре, без морфологии и без подсветки. Этого
 * хватает: ищут в документации по имени метода или параметра, а не по фразе.
 */
function initSearch(root, index) {
  root.querySelectorAll('[data-docs-search]').forEach((box) => {
    const input = box.querySelector('.docs-search__input');
    const results = box.querySelector('[data-docs-search-results]');
    const list = box.querySelector('[data-docs-search-list]');
    const empty = box.querySelector('[data-docs-search-empty]');

    if (!input || !results || !list || !empty) {
      return;
    }

    const render = (matches) => {
      list.replaceChildren();

      matches.forEach((item) => {
        const li = document.createElement('li');
        const link = document.createElement('a');

        link.className = 'docs-search__link';
        link.href = item.url;
        link.textContent = item.label;

        const page = document.createElement('span');
        page.className = 'docs-search__page';
        page.textContent = item.page;
        link.appendChild(page);

        li.appendChild(link);
        list.appendChild(li);
      });

      empty.hidden = matches.length > 0;
      results.hidden = false;
    };

    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();

      if (query.length < SEARCH_MIN_LENGTH) {
        results.hidden = true;

        return;
      }

      render(
        index
          .filter((item) => (item.label + ' ' + item.page + ' ' + item.text).toLowerCase().includes(query))
          .slice(0, SEARCH_LIMIT),
      );
    });

    // Esc закрывает выдачу, не очищая поле: запрос чаще хотят поправить, чем набрать заново.
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        results.hidden = true;
      }
    });

    // Клик мимо — тоже закрытие. Слушаем на документе, потому что попасть мимо можно куда угодно.
    document.addEventListener('click', (event) => {
      if (!box.contains(event.target)) {
        results.hidden = true;
      }
    });
  });
}

/**
 * Подсветка читаемого сейчас раздела в левом меню.
 *
 * IntersectionObserver, а не пересчёт на scroll: на странице Payments API три десятка
 * заголовков, и считать их положение на каждый кадр прокрутки — заметная работа впустую.
 *
 * rootMargin сдвигает «читаемую» полосу к верхней трети экрана: иначе подсветка перескакивала
 * бы на следующий раздел, когда его заголовок только показался внизу.
 */
function initActiveSection() {
  const links = Array.from(document.querySelectorAll('[data-docs-anchor]')).filter(
    (link) => link.dataset.docsAnchor,
  );

  if (links.length === 0 || !('IntersectionObserver' in window)) {
    return;
  }

  const byAnchor = new Map();

  links.forEach((link) => {
    const anchor = link.dataset.docsAnchor;

    if (!byAnchor.has(anchor)) {
      byAnchor.set(anchor, []);
    }

    byAnchor.get(anchor).push(link);
  });

  const setCurrent = (anchor) => {
    links.forEach((link) => link.classList.toggle('is-current', link.dataset.docsAnchor === anchor));
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (visible.length > 0) {
        setCurrent(visible[0].target.id);
      }
    },
    { rootMargin: '-80px 0px -66% 0px', threshold: 0 },
  );

  byAnchor.forEach((_, anchor) => {
    const heading = document.getElementById(anchor);

    if (heading) {
      observer.observe(heading);
    }
  });
}

/**
 * Где показать страницу сразу после перехода.
 *
 * Правило: адрес без якоря — всегда начало страницы, адрес с якорем — этот раздел. Само по
 * себе ни то, ни другое не гарантировано. Переход по прямой ссылке на раздел
 * (`/documentation/webhooks/#notifications`) открывал страницу В САМОМ ВЕРХУ — воспроизведено
 * до этой правки: браузерный переход к фрагменту разъезжается с анимированной прокруткой,
 * которую включает `scroll-behavior: smooth` у Bootstrap. А открытие страницы без якоря
 * читатель видел на той же высоте, на которой оставил предыдущую страницу документации.
 *
 * Только для ПЕРЕХОДА: у «назад/вперёд» и перезагрузки восстановление позиции не трогаем —
 * там читатель возвращается на своё место осознанно, и отматывать его наверх было бы хамством.
 *
 * behavior: 'instant' обязателен по той же причине, что и сама функция: со `smooth` страница
 * открывалась бы прокруткой откуда-то, хотя читатель ещё ничего не нажимал.
 */
function initInitialScroll() {
  const [entry] = performance.getEntriesByType('navigation');

  if (entry && entry.type !== 'navigate') {
    return;
  }

  const anchor = window.location.hash.slice(1);

  if (!anchor) {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    return;
  }

  // Якорь в адресе закодирован (кириллицы в наших якорях нет, но адрес мог прийти извне).
  let target = null;

  try {
    target = document.getElementById(decodeURIComponent(anchor));
  } catch (error) {
    target = document.getElementById(anchor);
  }

  // Отступ под шапку даёт scroll-margin-top у заголовка — здесь его повторять не нужно.
  target?.scrollIntoView({ behavior: 'instant', block: 'start' });
}

/**
 * Мобильное меню документации закрывается по клику на любой его пункт.
 *
 * Для перехода на другой раздел это просто вежливость (иначе меню висит поверх, пока грузится
 * страница), а для якоря ТОЙ ЖЕ страницы — необходимость: пока offcanvas открыт, bootstrap
 * держит на body `overflow: hidden`, и прокрутка к разделу под ним не видна вовсе — читатель
 * нажимал пункт и оставался в том же меню.
 *
 * Поэтому у якоря своей страницы переход берём на себя: гасим меню, а к разделу едем, когда
 * оно закрылось (тот же приём, что у бургера главной в main.js). Адрес при этом дописываем
 * сами — без него ссылка на раздел не попала бы в историю.
 */
function initOffcanvasLinks() {
  const offcanvas = document.getElementById('offcanvasDocs');

  if (!offcanvas) {
    return;
  }

  offcanvas.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');

    if (!link || !offcanvas.contains(link)) {
      return;
    }

    const instance = Offcanvas.getInstance(offcanvas);
    const isSamePageAnchor = link.hash && link.pathname === window.location.pathname;

    if (isSamePageAnchor) {
      event.preventDefault();

      offcanvas.addEventListener(
        'hidden.bs.offcanvas',
        () => {
          window.history.pushState(null, '', link.href);
          document.getElementById(link.hash.slice(1))?.scrollIntoView();
        },
        { once: true },
      );
    }

    instance?.hide();
  });
}

/** Индекс поиска лежит в <script type="application/json"> — один на страницу, оба меню читают его. */
function readSearchIndex() {
  const node = document.getElementById('docsSearchIndex');

  if (!node) {
    return [];
  }

  try {
    const parsed = JSON.parse(node.textContent || '[]');

    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

export function initDocumentation() {
  const page = document.querySelector('.docs-page');

  if (!page) {
    return;
  }

  // Меню на странице два (боковое и мобильное), и поведение нужно обоим. Корнем берём
  // document: мобильное меню лежит вне .docs-page — offcanvas у bootstrap соседний элемент.
  initAccordion(document);
  initCopy(document);
  initToggles(document);
  initSearch(document, readSearchIndex());
  initActiveSection();
  initOffcanvasLinks();
  // Последним: подсветка читаемого раздела считается от положения страницы, а оно этим
  // вызовом и определяется.
  initInitialScroll();
}
